/**
 * Minimal i18n: plain object dictionary, no external library.
 *
 * Per project rule: English is the default; the Russian toggle is only shown
 * to admins. Clients always see English.
 */

export type Locale = 'en' | 'ru';

type Dict = Record<string, string>;

const en: Dict = {
  navDevices: 'Devices',
  navDisks: 'Disks',
  navBackups: 'Backups',
  disksSelectDevice: 'Select a device',
  disksSwitchTitle: 'Switch device?',
  disksSwitchBody: 'You have unsaved changes that will be lost if you switch to another device.',
  disksSwitchOk: 'Discard & switch',
  cancel: 'Cancel',
  disksPickPrompt: 'Select a device to read its installed disks live.',
  disksOpening: 'Reading disks live…',
  disksReadFailed: 'Could not read the disks. Select a device to try again.',
  navUsers: 'Users',
  historyStage: 'Stage',
  historyState: 'State',
  historyLastBackup: 'Last backup',
  historyCount: 'Backups',
  stageReported: 'reported',
  stageBefore: 'before flash',
  stageAfter: 'after flash',
  stageManual: 'manual',
  stageUploaded: 'uploaded',
  stageImported: 'imported',
  historyAppliedBy: 'Applied by',
  historyAuto: 'auto',
  more: 'more',
  historyDownload: 'Download',
  historyAnyDevice: 'Any device',
  historyAnySource: 'Any stage',
  historyDevice: 'Device',
  navLogout: 'Log out',
  loginTitle: 'Sign in',
  loginEmail: 'Email',
  loginUsername: 'Username',
  loginPassword: 'Password',
  loginSubmit: 'Sign in',
  loginBadCredentials: 'Invalid username or password',
  loginSsoBtn: 'Sign in with SSO',
  sessionsTitle: 'Disks',
  sessionId: 'Session',
  sessionDevice: 'Device',
  sessionStatus: 'Status',
  sessionCreated: 'Created',
  sessionOpen: 'Open',
  emptyList: 'No items yet',
  sessionEditorTitle: 'Disk editor',
  diskBom: 'BOM',
  bomLenWarn: 'BOM must be 8 chars (now {n})',
  bomLenHint: 'The BarCode material field is exactly 8 characters, so "sync BOM" only applies an 8-char BOM.',
  wlTitle: 'Whitelist',
  wlType: 'Whitelist type',
  wlVersion: 'Whitelist version',
  wlBomRef: 'Whitelist BOM',
  wlSectionTitle: 'Whitelist',
  wlHint: 'Disk whitelist (opdiskwl). Edit the list type/version; the whitelist BOM tracks the BOM field above and is written on Apply. Written to both slots.',
  wlValueWarn: 'Avoid spaces, quotes, comma or semicolon here.',
  certOrig: 'Orig',
  certOur: 'Our',
  certError: 'Cert?',
  hdrValid: 'Valid',
  hdrCert: 'Cert',
  hdrStatus: 'Status',
  certStatusHint: 'DISK_AUTHENTICATION certificate (r4): Orig = signed by the genuine Huawei CA, Our = signed by our re-issued OEM CA, Cert? = other/invalid or the CN does not match the disk serial. Expiry is not checked.',
  certImportedBadge: 'Cert imported',
  certImportedHint: 'A new DISK_AUTHENTICATION certificate (r4/r5) was imported from a .hwrsv and will be written on Apply.',
  certToOur: '→ Our',
  certToOrig: '→ Orig',
  certNone: '(no certificate)',
  certChange: 'Change cert',
  certChangeSelected: 'Change cert',
  certBulkConfirm: 'Re-issue the certificate on {n} selected disk(s) to {target}? Written on Apply. Ineligible disks are skipped.',
  certBulkStaged: '{n} disk(s) staged for a certificate change',
  certBulkFailed: '{n} disk(s) could not be changed',
  certBulkNone: 'No eligible disks among the selection ({n} skipped)',
  certConvertTo: '→ {name}',
  certRestoreOriginal: '→ Original (from backup)',
  certToCaConfirm: 'Re-issue this disk\'s certificate onto "{name}"? It will be written on Apply.',
  certNewCa: '{name} (re-issued)',
  certNewOur: 'Our (re-issued OEM CA)',
  certNewOrig: 'Original — restored from backup ({source}, {when})',
  backupSrc_pre_apply: 'before flashing',
  backupSrc_inventory_snapshot: 'reported by device',
  backupSrc_post_apply_snapshot: 'after flashing',
  backupSrc_manual: 'manual',
  certToOurConfirm: 'Re-issue this disk\'s certificate onto our OEM CA? It will be written on Apply.',
  certToOrigConfirm: 'Restore the original Huawei certificate from a backup? It will be written on Apply.',
  certConvertOk: 'Prepare',
  certConvertHint: 'Stage a certificate change (r4/r5). Nothing is written until you Apply.',
  certConvertFailed: 'Certificate conversion failed',
  certStagedOur: 'Our certificate staged — Apply to write it',
  certStagedOrig: 'Original certificate staged — Apply to write it',
  certStagedHint: 'A certificate change is staged (r4/r5) and will be written on Apply.',
  certClearStaged: 'Discard the staged certificate change',
  certRegion: 'Certificate (r4/r5)',
  certOnDisk: '(current on disk)',
  certImported: '(imported from .hwrsv)',
  fieldSelfKeyWarn: 'Value starts with "{key}=" — looks like a whole elabel line was pasted. Keep only the value, not the key.',
  fieldForeignKeyWarn: 'Value starts with "{key}=" — looks like another field\'s line was pasted here.',
  fieldNewlineWarn: 'Value contains a line break — it would split this into a phantom elabel field.',
  fieldEdgeSpaceWarn: 'Value has leading/trailing spaces the original did not — they would be written to the disk.',
  diskSlot: 'Slot',
  diskNode: 'Node',
  diskFrameHint: 'Physical frame/enclosure the disk sits in (from the controller).',
  diskSlotHint:
    'Physical bay reported by the controller. The disk table only refreshes when a disk is physically re-seated, so a just-flashed disk may still show its old slot until it is pulled and reinserted.',
  diskNodeIdHint: 'Linux device-node ID (the number in /dev/nvme<ID>n1a).',
  diskIntegrityHint:
    'Integrity checks: {e} error(s), {w} warning(s) across CRC-32, T10 PI Guard and the e-label.',
  diskIntegrityClickHint: 'Click to see which integrity checks failed.',
  diskIntegrityDetailTitle: 'Integrity issues',
  integrityError: 'ERROR',
  integrityWarning: 'WARN',
  diskIntegrityNaHint: 'Integrity report unavailable for this disk.',
  diskValidHint: "Controller status from `sddebug showdisk` (Valid column). \"Normal\" = the controller accepts the disk identity; anything else (Level/BOM/…) means it still sees a mismatch. Stale until the disk is power-cycled after a flash.",
  diskTypeHint: 'Disk type: {type}',
  diskLevel: 'Level',
  diskBarcode: 'BarCode',
  diskModel: 'Model',
  diskDescription: 'Description',
  diskManufactured: 'Manufactured',
  diskBoardType: 'BoardType',
  diskBarcodeDate: 'BarCode date',
  diskBarcodeInvalid: 'malformed barcode',
  diskBarcodeInvalidTitle: 'The assembled barcode does not match the fixed Huawei layout; the server will reject it.',
  dragOrderHint: 'drag cards to set the iteration order',
  sortBy: 'Sort',
  sortSlot: 'Slot',
  sortId: 'Node ID',
  sortSn: 'Serial',
  sortBarcode: 'BarCode',
  sortManual: 'Manual (drag)',
  sortDesc: 'Descending',
  batchBarcodeTitle: 'BarCode counter',
  batchBarcodeHint:
    'Always on: BarCode is assembled per disk from BOM + Manufactured date + a serial that increments by 1 in card order (the barcode serial — not the disk SN). Defaults to the serial of the first card.',
  batchBarcodeStart: 'start serial (6 chars)',
  batchBarcodeSrcHint: 'other BarCode fields are kept from each disk',
  batchBarcodeManualSkip: '{sn}: BarCode in manual mode — skipped',
  batchBarcodeNoBarcode: '{sn}: no BarCode on disk — skipped',
  batchBarcodeOverflow: '{sn}: serial counter overflow — skipped',
  buttonApply: 'Apply changes',
  buttonHistory: 'History',
  applyFailed: 'Apply failed',
  hexViewLabel: 'Hex view (admin only)',
  backupsTitle: 'Details —',
  backupSource: 'Kind',
  backupCreated: 'Created',
  backupSummary: 'Summary',
  restoreConfirm: 'Send this snapshot back to the device as the response. The device will write it byte-for-byte.',
  flashThis: 'Flash',
  flashSent: 'Flash queued — the device will write it on its next poll.',
  backupUploadBtn: 'Upload .hwrsv',
  backupUploadHint: 'Upload a .hwrsv archive for this disk — it is stored as a manual backup you can flash from the list.',
  backupUploadOk: 'Backup uploaded — flash it from the list.',
  backupSnapshotBtn: 'Back up now',
  backupSnapshotOk: 'Backup captured.',
  loadingEllipsis: 'Loading…',
  pwChangeRequiredTitle: 'Password change required',
  pwChangeRequiredHint:
    'Your account ({email}) is using a temporary or default password. Set a new password to continue.',
  pwCurrent: 'Current password',
  pwNew: 'New password',
  pwConfirm: 'Confirm new password',
  pwMinHint: 'At least 8 characters',
  pwMismatch: 'Passwords do not match',
  pwChangeSubmit: 'Change password',
  pwChanged: 'Password changed',
  navAudit: 'Logs',
  themeToLight: 'Switch to light theme',
  themeToDark: 'Switch to dark theme',
  devicesTitle: 'Registered devices',
  userQuota: 'Quota',
  quotaGrantOk: 'Quota granted to {email}; now available: {available}',
  quotaRevokeOk: 'Quota revoked from {email}; now available: {available}',
  userImpersonate: 'Sign in as',
  impersonateConfirmTitle: 'Sign in as this client?',
  impersonateConfirmBody: 'You will connect to {name}\'s account and see everything as they do.',
  impersonateBlockLabel: 'Block the client while I work (their UI is locked and shows a notice)',
  impersonateConfirmOk: 'Connect',
  impersonateBlockedTitle: 'Session temporarily locked',
  impersonateBlockedBody: 'Administrator {name} is currently working under your account. Your interface is temporarily locked and will unlock automatically.',
  chatTitle: 'Support chat',
  chatWithName: 'Chat with {name}',
  chatWithSupport: 'Chat with support',
  chatThreads: 'Conversations',
  chatClear: 'Clear',
  chatClearConfirm: 'Delete all messages in this conversation? This cannot be undone.',
  chatPlaceholder: 'Type a message…',
  chatSend: 'Send',
  chatEmpty: 'No messages yet. Say hi 👋',
  chatNoThreads: 'No conversations yet',
  chatYou: 'You',
  chatBack: 'Back',
  navConsole: 'Console',
  navSettings: 'Settings',
  settingsIntro: 'System-wide settings. Changes take effect immediately for everyone.',
  settingsFeedbackTitle: 'Admin feedback',
  settingsFeedbackLabel: 'Enable the feedback chat',
  settingsFeedbackHint:
    'Show the floating feedback/chat widget so clients can message admins. When off, the widget is hidden for everyone and new messages are rejected.',
  settingsSaved: 'Settings saved',
  settingsCertsTitle: 'Disk certificates',
  settingsCertsIntro:
    'Certificate authorities used to re-sign disk certificates. Each is generated as a clone of the genuine Huawei chain (fresh keys), so disks signed by it are recognised by name. Disks can be switched between any CA or restored to the original.',
  certCaName: 'Authority',
  certCaFingerprint: 'Intermediate fingerprint',
  certCaFlashed: 'Disks flashed',
  certCaDefault: 'default',
  certCaBuiltin: 'built-in',
  certCaNamePlaceholder: 'New certificate name',
  certCaGenerate: 'Generate',
  certCaSetDefault: 'Make default',
  certCaRename: 'Rename',
  certCaDelete: 'Delete',
  certCaCreated: 'Certificate authority generated',
  certCaDeleted: 'Certificate deleted ({n} disks were flashed with it)',
  certCaDeleteTitle: 'Delete certificate "{name}"?',
  certCaDeleteWarn1: '{n} disk(s) were flashed with this certificate.',
  certCaDeleteWarn2: 'Those disks will show an unknown certificate until re-flashed with another CA.',
  certCaDeleteWarn3: 'This is permanent — the signing key is destroyed and cannot be recovered.',
  certCaDeleteConfirmLabel: 'Type the certificate name to confirm deletion:',
  certCaDeleteConfirmHint: 'Enter exactly: {name}',
  deviceConsole: 'Console',
  deviceConsoleTitle: 'Device console — {name}',
  deviceConsoleHint: 'Commands run on the device on its next heartbeat; output returns on a later beat (a few seconds of lag).',
  deviceConsolePlaceholder: 'Enter a command and press Enter…',
  deviceConsoleRunning: 'running…',
  deviceConsoleEmpty: 'No commands yet.',
  deviceConsoleClear: 'Clear',
  consoleTitle: 'Server console',
  consoleOnline: 'connected',
  consoleConnecting: 'connecting…',
  consoleClosed: 'disconnected',
  consoleReconnect: 'Reconnect',
  consoleUnavailable: 'Console unavailable',
  consoleUnavailableHint: 'The node-pty native module is not built on this host. Install build tools (apt install -y build-essential python3) and reinstall dependencies.',
  impersonationBanner: 'Viewing as {email}',
  impersonationReturn: 'Back to admin',
  logsAnyDevice: '(any device)',
  logsAnyUser: '(any user)',
  logsSearchPlaceholder: 'search: disk SN, text…',
  logsLoadMore: 'Load more',
  logsAllLoaded: 'all records loaded',
  deviceId: 'Device',
  deviceStatus: 'Status',
  deviceFingerprint: 'Device ID',
  deviceActions: 'Actions',
  colActions: 'Actions',
  deviceLastErrorBadge: 'error',
  deviceLastSeen: 'Last seen',
  deviceLabel: 'Name',
  deviceRename: 'Rename',
  deviceUnnamed: '(unnamed)',
  deviceOpenDisks: 'Disks',
  deviceOpenDisksHint: 'Read the installed disks live from the device',
  deviceOfflineRead: 'Device is offline — disks are read live, so it must be online.',
  fwPublishBtn: 'Publish firmware',
  fwPublishTitle: 'Publish firmware release',
  fwPublishOk: 'Publish',
  fwVersion: 'Version (optional — auto-detected)',
  fwVersionAutoPlaceholder: 'auto-detected from the binary',
  fwBinary: 'Binary (aarch64)',
  fwMandatory: 'Mandatory update',
  fwNotes: 'Release notes (optional)',
  fwNoFile: 'Choose a binary file first.',
  fwNoVersion: 'Enter a version.',
  fwPublished: 'Published v{version} ({size} bytes)',
  fwHint: 'The version is read straight from the uploaded binary’s embedded marker, so a release can never be mislabelled. Leave the field blank; fill it only to assert an expected value (a mismatch is rejected).',
  fwSize: 'Size',
  fwMinProto: 'Min protocol',
  fwPublishedAt: 'Published',
  fwPageTitle: 'Firmware releases',
  fwEmpty: 'No firmware published yet.',
  fwEditBtn: 'Edit',
  fwEditTitle: 'Edit release {version}',
  fwSaveBtn: 'Save',
  fwSaved: 'Release updated.',
  fwDeleteBtn: 'Delete',
  fwDeleteConfirm: 'Delete release {version}? Devices already on it are unaffected — it just stops being offered.',
  fwDeleted: 'Release deleted.',
  navFirmware: 'Firmware',
  deviceReadTimeout: 'The device did not return a fresh disk read in time (busy or just went offline).',
  editorReload: 'Reload',
  editorReloadConfirm: 'Ask the device to re-scan its disks? All unsaved changes will be lost.',
  editorReloadOffline: 'Device is offline — showing the last known data. It will re-scan once it reconnects.',
  editorReloadBtn: 'Re-read disks',
  editorStaleFlashTitle: 'Flashed by {user} just now',
  editorStaleFlashBody: 'Another operator flashed this device after you opened it — your disk view is stale. Re-read before applying, or your changes may overwrite theirs.',
  presenceOpenBy: 'open by {users}',
  editorReloadTimeout: 'No fresh inventory yet — the device may be busy or offline. Showing the latest available.',
  copySerial: 'Copy serial number',
  copied: 'Copied to clipboard',
  copyFailed: 'Copy failed',
  editorDisksRefreshed: 'Disks re-read — showing the new state.',
  editorPostApplyWaiting: 'Waiting for the disks to re-inventory with the new values…',
  editorPostApplyWaitingHint:
    'The flash succeeded and the list will refresh by itself once the device re-reads the disks. If a power-cycle was requested the disks reboot first, so this can take up to ~2.5 minutes. No need to reload manually.',
  editorDevice: 'Device',
  editorApplying: 'Applying — writing to the device…',
  editorApplyingHint:
    'The disk list is locked until the device reports back. After writing, each disk is power-cycled and takes ~10–15s to come back online.',
  editorApplyOk: 'Changed disks flashed successfully',
  editorApplyFailed: 'Some changed disks failed to flash',
  editorApplyCount: 'Flashed {flashed} changed disk(s); {unchanged} unchanged were left untouched.',
  pcBackInSec: 'power-cycled, back in {sec}s',
  pcNoReturn: 'power-cycled, did not return in time',
  pcFailed: 'power-cycle failed',
  pcSkipped: 'power-cycle skipped',
  pcCycleDiskHint: 'Power-cycle this disk (reboots the live disk)',
  pcCycleSelected: 'Power-cycle selected',
  pcCycleSelectedHint: 'Power-cycle the selected disks (reboots live disks)',
  pcSlotUnknown: 'Slot unknown — update the agent to power-cycle this disk',
  pcConfirmTitle: 'Power-cycle disks?',
  pcConfirmWarning: 'This will reboot {count} live disk(s). They will go offline for ~10–15s while they restart.',
  pcConfirmSlots: 'Disks',
  pcConfirmOk: 'Power-cycle',
  pcCycleRequested: 'Power-cycling {serials} — waiting for the disks to return…',
  pcCycleTimeout: 'Disks did not re-inventory in time — they may still be returning. Reload to check.',
  pcWaiting: 'Power-cycling {serials} — waiting for the disks to return…',
  pcWaitingHint:
    'The disks reboot and the agent re-inventories once they return (~10–15s each). This view will switch to the fresh session automatically.',
  downloadGroupBackup: 'Download group backup (.hwrsv)',
  devicePresence: 'Presence',
  deviceApprove: 'Approve',
  deviceDeny: 'Deny',
  deviceDenyConfirm: 'Deny this connection request?',
  deviceOnline: 'online',
  deviceOffline: 'offline',
  deviceVersion: 'Version',
  devicePendingCount: '{n} awaiting approval',
  deviceUpdateBtn: 'Update',
  deviceUpdateDialogTitle: 'Update {label}',
  deviceUpdateCurrentVersion: 'Current version',
  deviceUpdateSelectVersion: 'Target version',
  deviceUpdateLatestTag: 'latest',
  deviceUpdateMandatoryTag: 'mandatory',
  deviceUpdateWhatsNew: "What's new",
  deviceUpdateNoNotes: 'No release notes for this version.',
  deviceUpdateNoReleases: 'No published releases yet.',
  deviceUpdatePending:
    'Automatic delivery to the device is not wired up yet — this only previews what would ship.',
  deviceUpdateNowBtn: 'Update now',
  deviceUpdateRequested: 'Update requested — the device will apply it on its next heartbeat.',
  deviceUpdateNowHint:
    'On its next heartbeat the device pulls the latest published release, verifies the signature, and swaps it in (with automatic rollback on failure).',
  deviceUpdateSuperadminOnly: 'Only a superadmin can trigger delivery to the device.',
  deviceLastError: 'Last Error',
  deviceErrorDialogTitle: 'Error on {label}',
  deviceErrorAt: 'Occurred at',
  deviceRecentLog: 'Recent log',
  deviceShowLog: 'Show Log',
  deviceVerboseEnable: 'Verbose log',
  deviceVerboseFor: 'Verbose for {n}m',
  deviceVerboseActive: 'Verbose: {n}m left',
  deviceVerboseDisable: 'Stop',
  deviceVerboseHint: 'Turn on verbose device logging for a limited time.',
  deviceLogDialogTitle: 'Log — {label}',
  deviceNoLogs: 'No logs yet',
  deviceLogLoadMore: 'Load more',
  deviceLogAllLoaded: 'all loaded',
  deviceOwnerUnassigned: 'Not assigned',
  deviceOwnerChange: 'Change',
  deviceOwnerAssign: 'Assign owners',
  deviceOwnerDialogTitle: 'Owners of {label}',
  deviceOwnerSave: 'Save',
  deviceOwnerNone: 'No owners',
  dialogClose: 'Close',
  auditTitle: 'Logs',
  auditAction: 'Action',
  auditTime: 'Time',
  auditUserDevice: 'User / Device',
  auditIp: 'IP',
  auditDetails: 'Details',
  auditExportCsv: 'Export CSV',
  recoveryBannerTitle: 'Low on recovery codes',
  recoveryBannerBody: 'Only {remaining} unused TOTP recovery codes left — regenerate before you run out.',
  recoveryBannerCta: 'Go to Account',
  sessionDelete: 'Delete',
  sessionDeleteConfirm: 'Delete this session permanently?',
  previewBtn: 'Preview',
  previewFailed: 'Preview failed',
  previewTitle: 'Patch preview (no changes applied)',
  previewBytesChanged: 'bytes changed',
  previewRegion: 'region',
  previewFirstOffset: 'first offset',
  quotaBalance: 'Quota',
  quotaExhaustedTitle: 'Quota exhausted',
  quotaShortTitle: 'Not enough quota for all selected disks',
  quotaFlashCount: 'Will flash {n} disk(s) · quota available: {available}',
  quotaShortHint:
    'Each flashed disk costs one quota unit. Uncheck disks so the count is at most {available}, or ask an admin to allocate more.',
  quotaExhaustedHint: 'You have no flashing quota left — ask an admin to allocate more. Apply is disabled until then.',
  quotaLowTitle: 'Low quota: {n} flash(es) left',
  quotaLowHint: 'Ask an admin to allocate more before you run out.',
  quotaTotal: 'Total',
  quotaUsed: 'Used',
  quotaRemaining: 'Remaining',
  quotaExpires: 'Expires',
  quotaNote: 'Note',
  quotaRevocationsTitle: 'Revoked',
  quotaRevokedAmount: 'Amount',
  quotaRevokedBy: 'By',
  quotaGranted: 'Granted at',
  quotaGrantedBy: 'Granted by',
  quotaGrantBtn: 'Grant quota',
  quotaGrantTitle: 'Grant a new quota',
  quotaCount: 'Units',
  quotaExpiry: 'Expires in days (blank = never)',
  quotaSubmit: 'Grant',
  quotaRevokeBtn: 'Revoke quota',
  quotaRevokeTitle: 'Revoke quota',
  quotaRevokeSubmit: 'Revoke',
  quotaRevokeAvailable: 'Available to revoke: {available}',
  // Navigation (mobile)
  navMenu: 'Menu',
  navOpenMenu: 'Open navigation menu',
  // Dashboard
  navDashboard: 'Dashboard',
  dashKpiFlashesToday: 'Flashes today',
  dashKpiFlashes7d: 'Flashes (7d)',
  dashKpiFlashes30d: 'Flashes (30d)',
  dashKpiFlashesTotal: 'Flashes total',
  dashKpiFlashesUnique: 'Unique disks flashed',
  dashKpiQuotaGranted: 'Quota granted',
  dashKpiQuotaConsumed: 'Quota consumed',
  dashKpiQuotaAvailable: 'Quota available',
  dashKpiActiveDevices: 'Active devices',
  dashKpiDevicesOnline: 'Devices online / total',
  dashKpiActiveClients: 'Active clients',
  dashChartTitle: 'Flashes per day',
  dashChartEmpty: 'No flashes in this range',
  dashChartFlashesOn: '{count} flash(es) on {date}',
  dashRange7: '7d',
  dashRange30: '30d',
  dashRange90: '90d',
  dashTopClients: 'Top clients by flashes',
  dashColClient: 'Client',
  dashTopDevices: 'Top devices by flashes',
  dashColDevice: 'Device',
  dashDeviceDeleted: 'deleted',
  dashColFlashes: 'Flashes',
  dashColUniqueDisks: 'Unique disks',
  dashColAvailable: 'Available',
  dashColUsed: 'Used',
  dashTotal: 'Total',
  dashRefresh: 'Refresh',
  // Dashboard — storage + disk population
  dashStorageTitle: 'Storage & disks',
  dashDisksByType: 'Flashed disks by type',
  dashDiskNvme: 'NVMe',
  dashDiskSas25: 'SAS 2.5"',
  dashDiskSas35: 'SAS 3.5"',
  dashDiskUnknown: 'Unknown',
  dashDisksTotal: 'Flashed total',
  dashBackupsCount: 'Backups',
  dashBackupsSize: 'Backups size',
  dashDbSize: 'Database size',
  dashServerFree: 'Server free space',
  dashServerUsedOf: '{used} of {total} used',
  // Quota history (ledger)
  quotaHistoryTitle: 'Quota history',
  quotaHistoryEmpty: 'No quota history yet',
  quotaLedgerDate: 'Date',
  quotaLedgerType: 'Type',
  quotaLedgerDelta: 'Change',
  quotaLedgerBalance: 'Balance after',
  quotaLedgerReason: 'Reason',
  quotaLedgerDisk: 'Disk / device',
  quotaTypeTopup: 'top-up',
  quotaTypeSpend: 'spend',
  quotaTypeAdjust: 'adjust',
  quotaTypeExpire: 'expire',
  // Edit user
  userEdit: 'Edit',
  userEditTitle: 'Edit user',
  userEditPasswordHint: 'Leave blank to keep the current password',
  userEditOk: 'User {email} updated',
  userSave: 'Save',
  confirmApplyTitle: 'Confirm changes',
  confirmApplyIntro: 'The following changes will be sent to the device and written to the disks. One unit of quota is consumed per flashed disk.',
  confirmApplyEmpty: 'No changes to apply.',
  confirmApplyPowerCycle: 'Power-cycle disks after flashing',
  confirmApplyPowerCycleHint:
    'Reboots each disk at the end so the controller reloads the Reserve Area and picks up the new identity. Without it, the controller keeps showing the old Level/BOM until the disk is re-seated.',
  confirmApplyTypeYes: 'Type YES to confirm flashing {n} disk(s)',
  confirmApplyButton: 'Confirm and apply',
  confirmApplyCancel: 'Cancel',
  changeDisk: 'Disk',
  changeField: 'Field',
  changeOld: 'Current',
  changeNew: 'New',
  navAccount: 'Account',
  accountTitle: 'Your account',
  totpSection: 'Two-factor authentication',
  totpEnabledNote: '2FA is ENABLED on this account. You will be asked for a 6-digit code at every login.',
  totpDisabledNote: '2FA is DISABLED. Enable it to protect your account from password-only attacks.',
  totpBeginBtn: 'Enable 2FA',
  totpDisableBtn: 'Disable 2FA',
  totpSecretLabel: 'Scan this QR or enter the secret into your authenticator app (Google Authenticator, Authy, 1Password, …):',
  totpSecretCopy: 'Copy secret',
  totpCodeInput: 'Enter the 6-digit code from your app',
  totpVerifyBtn: 'Verify and enable',
  totpFailed: 'Wrong code. Try again — the code rotates every 30 seconds.',
  totpEnabledOk: '2FA is now enabled. Sign out and back in to test it.',
  deviceRevokeBtn: 'Revoke',
  deviceReactivateBtn: 'Reactivate',
  deviceRevokeConfirm: 'Revoke this device? All further envelopes it sends will be rejected with 403. Its audit history and disk backups stay intact.',
  deviceReactivateConfirm: 'Reactivate this device?',
  deviceDeleteBtn: 'Delete',
  deviceMigrateIdBtn: 'Migrate ID',
  deviceMigrateIdHint:
    'Move this controller onto a new device id (its stable hardware id), carrying its whole history across.',
  deviceMigrateIdTitle: 'Migrate device ID',
  deviceMigrateIdBody:
    'Enter the target device id — the controller’s hardware id (shown as Device ID on its local page, or its config hw_fingerprint). All history (sessions, backups, flash ledger, logs) is repointed. If the id is free the row is renamed (keys kept, no re-approval); if it already exists, history is merged into it.',
  deviceMigrateIdCurrent: 'Current id:',
  deviceMigrateIdOk: 'Migrate',
  deviceMigrateDone: 'Migrated ({mode}) — {n} history rows moved.',
  deviceDeleteConfirm:
    'Permanently delete this revoked device? Its sessions and operational data are removed; disk backups and audit history are kept.',
  authSessionsTitle: 'Active login sessions',
  authSessionId: 'Session',
  authSessionCreated: 'Created',
  authSessionExpires: 'Expires',
  authSessionCurrent: 'Current',
  authSessionRevoke: 'Revoke',
  authSessionRevokeConfirm: 'Revoke this session?',
  authSessionDevice: 'Device',
  authSessionIp: 'IP address',
  authSessionCount: 'Sessions',
  authSessionLastActive: 'Last sign-in',
  authSessionUnknownDevice: 'Unknown device',
  authSessionRevokeGroup: 'Revoke',
  authSessionRevokeGroupConfirm: 'Revoke all sessions from this device?',
  authSessionRevokeOthers: 'Revoke all others',
  authSessionRevokeOthersConfirm: 'Sign out everywhere except this browser?',
  recoveryCodesTitle: 'Recovery codes',
  recoveryCodesIntro:
    'Single-use codes that let you sign in if you lose access to your TOTP app. Print them or save them somewhere safe — they are shown only once.',
  recoveryCodesRemaining: 'Codes remaining',
  recoveryCodesIssuedNote: 'These codes are shown now and will NOT be shown again. Save them.',
  recoveryCodesCopy: 'Copy to clipboard',
  recoveryCodesDownload: 'Download .txt',
  recoveryCodesHide: 'I saved them',
  recoveryCodesRegenBtn: 'Regenerate codes',
  recoveryCodesRegenConfirm: 'This invalidates every existing code.',
  errorBoundaryTitle: 'Something went wrong',
  errorBoundaryIntro:
    'The dashboard hit an unexpected error. We sent a redacted report to the admin team.',
  errorBoundaryReload: 'Reload page',
  errorBoundaryDetails: 'Technical details',
  loginLocked: 'Locked',
  loginLockedExplain:
    'Too many failed attempts. Try again in {time}, or contact an admin if you forgot your password.',
  // Users management
  usersTitle: 'Users',
  userEmail: 'Email',
  userRole: 'Role',
  userCreated: 'Created',
  userActions: 'Actions',
  userCreateTitle: 'New user',
  userCreateBtn: 'Create user',
  userNewBtn: 'New user',
  userResetPw: 'Reset password',
  userResetPwPrompt: 'New password for {email}:',
  userResetPwOk: 'Password reset for {email}',
  userDelete: 'Delete',
  userDeleteConfirm: 'Delete user {email}? This cannot be undone.',
  userRoleAdmin: 'admin',
  userRoleClient: 'client',
  userRoleSuperadmin: 'superadmin',
  userNote: 'Note',
  userNotePlaceholder: 'Optional note (org, contact, purpose)…',
  auditClearBtn: 'Clear log',
  auditClearConfirm: 'Permanently delete ALL audit log entries? This cannot be undone.',
  auditClearedOk: 'Audit log cleared ({removed} entries)',
  backupDelete: 'Delete',
  backupDeleteConfirm: 'Permanently delete this backup?',
  backupDeletedOk: 'Backup deleted',
  userPasswordPlaceholder: 'password',
  userPasswordMinHint: 'At least 8 characters.',
  userYouTag: '(you)',
  userActionFailed: 'Action failed',
  // Batch disk edit
  batchTitle: 'Batch edit',
  batchHint: 'Fill the fields you want and apply them to all selected disks at once.',
  batchApply: 'Apply to selected ({n})',
  batchSelectAll: 'Select all',
  batchClear: 'Clear selection',
  batchSelected: '{n} selected',
  batchNothingSelected: 'Select at least one disk first.',
  diskModified: 'modified',
  clientEmptyHint:
    'Nothing here yet? Disks become visible after an administrator assigns a device to your account. Contact your administrator.',
  // Table-based disk editor
  bulkFillTitle: 'Bulk fill',
  bulkFillValue: 'value',
  bulkFillApply: 'Fill selected ({n})',
  barcodeCounterApply: 'Apply counter ({n})',
  colVisibility: 'Columns',
  colType: 'Type',
  colOrder: 'Order',
  colFrame: 'Frame',
  colSlot: 'Slot',
  buttonHex: 'Hex',
  hexDrawerTitle: 'Hex view — {sn}',
  moveUp: 'Move up',
  moveDown: 'Move down',
  barcodeManual: 'manual',
  barcodeSyncDate: 'sync date',
  barcodeSyncDateHint:
    'On by default: the BarCode date is recomputed from the Manufactured field. Untick to keep the disk’s stored BarCode date untouched.',
  barcodeSyncBom: 'sync BOM',
  barcodeSyncBomHint:
    'On by default: a part of the BarCode (8 chars) is overwritten from the current BOM field. Untick to leave that BarCode data untouched.',
  reorderManualHint: 'drag the grip or use the arrows to set the iteration order',
  dragHandle: 'Drag to reorder',
  undoTooltip: 'Undo last change',
  undoButton: 'Undo',
  // .hwrsv export / import (admin)
  hwrsvExport: 'Export',
  hwrsvImport: 'Import',
  hwrsvExportHint: 'Download this disk’s snapshot as a .hwrsv archive.',
  hwrsvImportHint: 'Upload a .hwrsv for this disk — it loads the file as pending changes (review, then Apply). It does not auto-flash.',
  hwrsvExportSelected: 'Export selected (.hwrsv)',
  hwrsvImportCombined: 'Import (.hwrsv)',
  hwrsvImportCombinedHint: 'Upload a .hwrsv; records are matched to disks by serial and loaded as pending changes (review, then Apply). Nothing is flashed automatically.',
  hwrsvExportOk: 'Backup downloaded.',
  hwrsvImportOk: 'Imported and registered as a backup — apply it from History.',
  hwrsvImportedPending: 'Imported {n} disk(s) as pending changes — review and Apply.',
  hwrsvExportFailed: 'Export failed',
  hwrsvImportFailed: 'Import failed',
  hwrsvImportedFields: 'Imported {n} field(s) as pending changes — review and Apply.',
  hwrsvImportedNone: 'Nothing to import — the file matched the disk’s current values.',
  // Username / identity + misc admin labels
  userUsername: 'Username',
  userEmailOptional: 'Email (optional)',
  deviceOwner: 'Owners',
  historyDiskSn: 'Disk SN',
  historyDiskType: 'Type',
  historyWhen: 'Date/Time',
  historyResult: 'Result',
  flashHistoryTitle: 'Flash history',
  flashHistoryLastFlash: 'Last flash',
  flashChanges: 'Changes',
  flashChangesCount: 'Disks',
  flashGroupByDevice: 'Group by device',
  flashPending: 'pending',
  flashOkAll: 'OK ({n})',
  flashPartial: '{ok}/{total} OK',
  flashUnknownDevice: 'Unknown device',
  navFlashHistory: 'Flash history',
  // XLSX export (disk editor + flash history)
  exportXls: 'Export XLSX',
  exportXlsTip: 'Download the disk list as a spreadsheet — two rows per disk: before / after',
  xlsSheetChanges: 'Changes',
  xlsSheetHistory: 'Flash history',
  xlsColSlot: 'Slot',
  xlsColFrame: 'Frame',
  xlsColChanged: 'Changed',
  xlsWasRow: 'was',
  xlsNowRow: 'new',
  xlsYes: 'yes',
  xlsNo: 'no',
  logsAllActors: '(all actors)',
  logsActionPlaceholder: 'action…',
  // Hex viewer
  hexRegionLabel: 'Region',
  hexLoading: 'Loading…',
  hexParsedTitle: 'Parsed (read-only)',
  hexParsedRegion: 'Region',
  hexParsedOffset: 'Offset',
  hexParsedLength: 'Length',
  hexBytesUnit: '{n} bytes',
  hexLegendBlock: 'block boundary ({n} B)',
  hexLegendPi: 'PI meta (last 8 B)',
  // Brand footer
  footerProject: 'Project',
  footerDocs: 'Documentation',
};

const ru: Dict = {
  navDevices: 'Устройства',
  navDisks: 'Диски',
  navBackups: 'Бэкапы',
  disksSelectDevice: 'Выберите устройство',
  disksSwitchTitle: 'Сменить устройство?',
  disksSwitchBody: 'Есть несохранённые изменения — они будут потеряны при переходе на другое устройство.',
  disksSwitchOk: 'Отменить изменения и перейти',
  cancel: 'Отмена',
  disksPickPrompt: 'Выберите устройство, чтобы прочитать его диски вживую.',
  disksOpening: 'Чтение дисков…',
  disksReadFailed: 'Не удалось прочитать диски. Выберите устройство и попробуйте снова.',
  historyStage: 'Этап',
  historyState: 'Состояние',
  historyLastBackup: 'Последний бэкап',
  historyCount: 'Бэкапов',
  stageReported: 'reported',
  stageBefore: 'before flash',
  stageAfter: 'after flash',
  stageManual: 'manual',
  stageUploaded: 'uploaded',
  stageImported: 'imported',
  historyAppliedBy: 'Кто применил',
  historyAuto: 'авто',
  more: 'ещё',
  historyDownload: 'Скачать',
  historyAnyDevice: 'Любое устройство',
  historyAnySource: 'Любой этап',
  historyDevice: 'Устройство',
  navUsers: 'Пользователи',
  navLogout: 'Выйти',
  loginTitle: 'Вход',
  loginEmail: 'Email',
  loginUsername: 'Имя пользователя',
  loginPassword: 'Пароль',
  loginSubmit: 'Войти',
  loginBadCredentials: 'Неверное имя пользователя или пароль',
  loginSsoBtn: 'Войти через SSO',
  sessionsTitle: 'Диски',
  sessionId: 'Сессия',
  sessionDevice: 'Устройство',
  sessionStatus: 'Статус',
  sessionCreated: 'Создана',
  sessionOpen: 'Открыть',
  emptyList: 'Пока пусто',
  sessionEditorTitle: 'Редактор дисков',
  diskBom: 'BOM',
  bomLenWarn: 'BOM должен быть 8 симв. (сейчас {n})',
  bomLenHint: 'Поле «материал» в BarCode ровно 8 символов, поэтому «sync BOM» применяет только 8-символьный BOM.',
  wlTitle: 'Whitelist',
  wlType: 'Whitelist type',
  wlVersion: 'Whitelist version',
  wlBomRef: 'Whitelist BOM',
  wlSectionTitle: 'Whitelist',
  wlHint: 'Whitelist диска (opdiskwl). Меняйте тип/версию списка; BOM в whitelist берётся из поля BOM выше и пишется при Apply. Пишется в оба слота.',
  wlValueWarn: 'Не используйте пробелы, кавычки, запятую и точку с запятой.',
  certOrig: 'Orig',
  certOur: 'Our',
  certError: 'Cert?',
  hdrValid: 'Valid',
  hdrCert: 'Серт',
  hdrStatus: 'Статус',
  certStatusHint: 'Сертификат DISK_AUTHENTICATION (r4): Orig — подписан оригинальным CA Huawei, Our — нашим пере-выпущенным OEM CA, Cert? — другой/невалидный или CN не равен серийному номеру диска. Срок действия не проверяется.',
  certImportedBadge: 'Серт импортирован',
  certImportedHint: 'Новый сертификат DISK_AUTHENTICATION (r4/r5) импортирован из .hwrsv и будет записан при применении.',
  certToOur: '→ Our',
  certToOrig: '→ Orig',
  certNone: '(нет сертификата)',
  certChange: 'Сменить серт',
  certChangeSelected: 'Сменить серт',
  certBulkConfirm: 'Перевыпустить сертификат на {n} выбранных диск(ах) на «{target}»? Запишется при применении. Неподходящие диски пропускаются.',
  certBulkStaged: 'Подготовлена смена сертификата на {n} диск(ах)',
  certBulkFailed: 'Не удалось сменить сертификат на {n} диск(ах)',
  certBulkNone: 'Среди выбранных нет подходящих дисков (пропущено: {n})',
  certConvertTo: '→ {name}',
  certRestoreOriginal: '→ Оригинал (из бэкапа)',
  certToCaConfirm: 'Перевыпустить сертификат диска на «{name}»? Будет записан при применении.',
  certNewCa: '{name} (перевыпущен)',
  certNewOur: 'Наш (пере-выпущенный OEM CA)',
  certNewOrig: 'Оригинал — восстановлен из бэкапа ({source}, {when})',
  backupSrc_pre_apply: 'перед прошивкой',
  backupSrc_inventory_snapshot: 'снимок от устройства',
  backupSrc_post_apply_snapshot: 'после прошивки',
  backupSrc_manual: 'вручную',
  certToOurConfirm: 'Перевыпустить сертификат диска на наш OEM CA? Будет записан при применении.',
  certToOrigConfirm: 'Восстановить оригинальный сертификат Huawei из бэкапа? Будет записан при применении.',
  certConvertOk: 'Подготовить',
  certConvertHint: 'Подготовить смену сертификата (r4/r5). Ничего не пишется до применения.',
  certConvertFailed: 'Не удалось сменить сертификат',
  certStagedOur: 'Наш сертификат подготовлен — примените, чтобы записать',
  certStagedOrig: 'Оригинальный сертификат подготовлен — примените, чтобы записать',
  certStagedHint: 'Смена сертификата подготовлена (r4/r5) и будет записана при применении.',
  certClearStaged: 'Отменить подготовленную смену сертификата',
  certRegion: 'Сертификат (r4/r5)',
  certOnDisk: '(текущий на диске)',
  certImported: '(импортирован из .hwrsv)',
  fieldSelfKeyWarn: 'Значение начинается с «{key}=» — похоже, вставлена вся elabel-строка. Оставьте только значение, без ключа.',
  fieldForeignKeyWarn: 'Значение начинается с «{key}=» — похоже, сюда вставлена строка другого поля.',
  fieldNewlineWarn: 'В значении есть перенос строки — он расщепит поле на фантомное в elabel.',
  fieldEdgeSpaceWarn: 'В начале/конце значения появились пробелы, которых нет в оригинале — они запишутся на диск.',
  diskSlot: 'Слот',
  diskNode: 'Нода',
  diskFrameHint: 'Физический фрейм/полка, где стоит диск (из контроллера).',
  diskSlotHint:
    'Физический слот по данным контроллера. Таблица дисков обновляется только при физической переустановке диска, поэтому только что прошитый диск может показывать старый слот, пока его не вынут и не вставят заново.',
  diskNodeIdHint: 'ID device-ноды Linux (число в /dev/nvme<ID>n1a).',
  diskIntegrityHint:
    'Проверки целостности: ошибок — {e}, предупреждений — {w} (CRC-32, T10 PI Guard и elabel).',
  diskIntegrityClickHint: 'Нажмите, чтобы увидеть, какие проверки целостности не прошли.',
  diskIntegrityDetailTitle: 'Проблемы целостности',
  integrityError: 'ОШИБКА',
  integrityWarning: 'ПРЕДУПР',
  diskIntegrityNaHint: 'Отчёт о целостности для этого диска недоступен.',
  diskValidHint: 'Статус контроллера из `sddebug showdisk` (колонка Valid). «Normal» — контроллер принял идентичность диска; иное (Level/BOM/…) — всё ещё видит рассинхрон. Обновляется только после паверцикла диска.',
  diskTypeHint: 'Тип диска: {type}',
  diskLevel: 'Level',
  diskBarcode: 'Штрихкод',
  diskModel: 'Model',
  diskDescription: 'Description',
  diskManufactured: 'Дата производства',
  diskBoardType: 'BoardType',
  diskBarcodeDate: 'Дата в BarCode',
  diskBarcodeInvalid: 'некорректный баркод',
  diskBarcodeInvalidTitle: 'Собранный баркод не соответствует фиксированной раскладке Huawei; сервер его отклонит.',
  dragOrderHint: 'перетащите карточки, чтобы задать порядок итерации',
  sortBy: 'Сортировка',
  sortSlot: 'Слот',
  sortId: 'ID ноды',
  sortSn: 'Serial',
  sortBarcode: 'Штрихкод',
  sortManual: 'Вручную (перетаскивание)',
  sortDesc: 'По убыванию',
  batchBarcodeTitle: 'Счётчик BarCode',
  batchBarcodeHint:
    'Всегда включён: BarCode собирается для каждого диска из BOM + даты производства + серийника, увеличивающегося на 1 в порядке карточек (баркодный серийник, не SN диска). По умолчанию — серийник первой карточки.',
  batchBarcodeStart: 'начальный серийник (6 симв.)',
  batchBarcodeSrcHint: 'остальные поля BarCode берутся из каждого диска',
  batchBarcodeManualSkip: '{sn}: BarCode в ручном режиме — пропущен',
  batchBarcodeNoBarcode: '{sn}: на диске нет BarCode — пропущен',
  batchBarcodeOverflow: '{sn}: переполнение счётчика — пропущен',
  buttonApply: 'Применить изменения',
  buttonHistory: 'История',
  applyFailed: 'Сбой применения',
  hexViewLabel: 'Hex-просмотр (только админ)',
  backupsTitle: 'Details —',
  backupSource: 'Тип',
  backupCreated: 'Создан',
  backupSummary: 'Состояние',
  restoreConfirm: 'Отправить этот снимок устройству как ответ. Устройство запишет его байт-в-байт.',
  flashThis: 'Прошить',
  flashSent: 'Прошивка поставлена в очередь — устройство запишет её при следующем опросе.',
  backupUploadBtn: 'Загрузить .hwrsv',
  backupUploadHint: 'Загрузите архив .hwrsv для этого диска — он сохранится как ручной бэкап, который можно прошить из списка.',
  backupUploadOk: 'Бэкап загружен — прошейте его из списка.',
  backupSnapshotBtn: 'Сделать бэкап',
  backupSnapshotOk: 'Бэкап создан.',
  loadingEllipsis: 'Загрузка…',
  pwChangeRequiredTitle: 'Требуется смена пароля',
  pwChangeRequiredHint:
    'Аккаунт ({email}) использует временный пароль или пароль по умолчанию. Задайте новый пароль, чтобы продолжить.',
  pwCurrent: 'Текущий пароль',
  pwNew: 'Новый пароль',
  pwConfirm: 'Повторите новый пароль',
  pwMinHint: 'Минимум 8 символов',
  pwMismatch: 'Пароли не совпадают',
  pwChangeSubmit: 'Сменить пароль',
  pwChanged: 'Пароль изменён',
  navAudit: 'Логи',
  themeToLight: 'Светлая тема',
  themeToDark: 'Тёмная тема',
  devicesTitle: 'Зарегистрированные устройства',
  userQuota: 'Квота',
  quotaGrantOk: 'Квота выдана {email}; доступно: {available}',
  quotaRevokeOk: 'Квота отозвана у {email}; доступно: {available}',
  userImpersonate: 'Войти как',
  impersonateConfirmTitle: 'Подключиться к аккаунту клиента?',
  impersonateConfirmBody: 'Вы подключитесь к аккаунту {name} и будете видеть всё так же, как он.',
  impersonateBlockLabel: 'Блокировать действия клиента на время работы админа (его интерфейс блокируется с уведомлением)',
  impersonateConfirmOk: 'Подключиться',
  impersonateBlockedTitle: 'Сессия временно заблокирована',
  impersonateBlockedBody: 'Под вашим аккаунтом сейчас работает администратор {name}. Ваш интерфейс временно заблокирован и разблокируется автоматически.',
  chatTitle: 'Чат поддержки',
  chatWithName: 'Чат с {name}',
  chatWithSupport: 'Чат с поддержкой',
  chatThreads: 'Диалоги',
  chatClear: 'Очистить',
  chatClearConfirm: 'Удалить все сообщения в этом диалоге? Действие необратимо.',
  chatPlaceholder: 'Введите сообщение…',
  chatSend: 'Отправить',
  chatEmpty: 'Сообщений пока нет. Напишите первым 👋',
  chatNoThreads: 'Диалогов пока нет',
  chatYou: 'Вы',
  chatBack: 'Назад',
  navConsole: 'Консоль',
  navSettings: 'Настройки',
  settingsIntro: 'Глобальные настройки системы. Изменения применяются сразу для всех.',
  settingsFeedbackTitle: 'Обратная связь с админом',
  settingsFeedbackLabel: 'Включить чат обратной связи',
  settingsFeedbackHint:
    'Показывать плавающий виджет обратной связи/чата, чтобы клиенты могли писать администраторам. Когда выключено, виджет скрыт для всех, а новые сообщения отклоняются.',
  settingsSaved: 'Настройки сохранены',
  settingsCertsTitle: 'Сертификаты дисков',
  settingsCertsIntro:
    'Центры сертификации для переподписи сертификатов дисков. Каждый генерируется как клон подлинной цепочки Huawei (новые ключи), поэтому подписанные им диски распознаются по имени. Диски можно переключать между любыми CA или возвращать к оригиналу.',
  certCaName: 'Центр',
  certCaFingerprint: 'Отпечаток intermediate',
  certCaFlashed: 'Прошито дисков',
  certCaDefault: 'по умолчанию',
  certCaBuiltin: 'встроенный',
  certCaNamePlaceholder: 'Название нового сертификата',
  certCaGenerate: 'Сгенерировать',
  certCaSetDefault: 'Сделать основным',
  certCaRename: 'Переименовать',
  certCaDelete: 'Удалить',
  certCaCreated: 'Центр сертификации сгенерирован',
  certCaDeleted: 'Сертификат удалён ({n} дисков было прошито им)',
  certCaDeleteTitle: 'Удалить сертификат «{name}»?',
  certCaDeleteWarn1: 'Этим сертификатом прошито дисков: {n}.',
  certCaDeleteWarn2: 'Эти диски будут показывать неизвестный сертификат, пока их не перепрошьют другим CA.',
  certCaDeleteWarn3: 'Это необратимо — ключ подписи уничтожается и не подлежит восстановлению.',
  certCaDeleteConfirmLabel: 'Введите название сертификата для подтверждения удаления:',
  certCaDeleteConfirmHint: 'Введите точно: {name}',
  deviceConsole: 'Консоль',
  deviceConsoleTitle: 'Консоль устройства — {name}',
  deviceConsoleHint: 'Команды выполняются на устройстве на его следующем heartbeat; вывод возвращается позже (задержка в несколько секунд).',
  deviceConsolePlaceholder: 'Введите команду и нажмите Enter…',
  deviceConsoleRunning: 'выполняется…',
  deviceConsoleEmpty: 'Команд пока нет.',
  deviceConsoleClear: 'Очистить',
  consoleTitle: 'Консоль сервера',
  consoleOnline: 'подключено',
  consoleConnecting: 'подключение…',
  consoleClosed: 'отключено',
  consoleReconnect: 'Переподключить',
  consoleUnavailable: 'Консоль недоступна',
  consoleUnavailableHint: 'Нативный модуль node-pty не собран на этом хосте. Установите инструменты сборки (apt install -y build-essential python3) и переустановите зависимости.',
  impersonationBanner: 'Вы смотрите от имени {email}',
  impersonationReturn: 'Вернуться в админа',
  logsAnyDevice: '(любое устройство)',
  logsAnyUser: '(любой пользователь)',
  logsSearchPlaceholder: 'поиск: SN диска, текст…',
  logsLoadMore: 'Загрузить ещё',
  logsAllLoaded: 'все записи загружены',
  deviceId: 'Устройство',
  deviceStatus: 'Статус',
  deviceFingerprint: 'Device ID',
  deviceActions: 'Действия',
  colActions: 'Действия',
  deviceLastErrorBadge: 'ошибка',
  deviceLastSeen: 'На связи',
  deviceLabel: 'Имя',
  deviceRename: 'Переименовать',
  deviceUnnamed: '(без имени)',
  deviceOpenDisks: 'Диски',
  deviceOpenDisksHint: 'Прочитать установленные диски с устройства вживую',
  deviceOfflineRead: 'Устройство офлайн — диски читаются вживую, оно должно быть на связи.',
  fwPublishBtn: 'Опубликовать прошивку',
  fwPublishTitle: 'Публикация прошивки',
  fwPublishOk: 'Опубликовать',
  fwVersion: 'Версия (необязательно — определится авто)',
  fwVersionAutoPlaceholder: 'определится из бинаря',
  fwBinary: 'Бинарь (aarch64)',
  fwMandatory: 'Обязательное обновление',
  fwNotes: 'Примечания к релизу (необязательно)',
  fwNoFile: 'Сначала выберите файл бинаря.',
  fwNoVersion: 'Укажите версию.',
  fwPublished: 'Опубликовано v{version} ({size} байт)',
  fwHint: 'Версия читается прямо из встроенного маркера загруженного бинаря, поэтому релиз невозможно подписать неверной версией. Поле оставьте пустым; заполняйте только чтобы подтвердить ожидаемое значение (при несовпадении — отказ).',
  fwSize: 'Размер',
  fwMinProto: 'Мин. протокол',
  fwPublishedAt: 'Опубликовано',
  fwPageTitle: 'Релизы прошивки',
  fwEmpty: 'Прошивки ещё не публиковались.',
  fwEditBtn: 'Изменить',
  fwEditTitle: 'Изменить релиз {version}',
  fwSaveBtn: 'Сохранить',
  fwSaved: 'Релиз обновлён.',
  fwDeleteBtn: 'Удалить',
  fwDeleteConfirm: 'Удалить релиз {version}? Устройства, уже на нём, не затрагиваются — он просто перестанет предлагаться.',
  fwDeleted: 'Релиз удалён.',
  navFirmware: 'Прошивки',
  deviceReadTimeout: 'Устройство не вернуло свежие данные вовремя (занято или только что отключилось).',
  editorReload: 'Перечитать',
  editorReloadConfirm: 'Попросить устройство пере-сканировать диски? Все несохранённые изменения будут потеряны.',
  editorReloadOffline: 'Устройство офлайн — показаны последние известные данные. Пере-сканирует при подключении.',
  editorReloadBtn: 'Перечитать диски',
  editorStaleFlashTitle: 'Только что прошито: {user}',
  editorStaleFlashBody: 'Другой оператор прошил это устройство после того, как вы его открыли — ваши данные устарели. Перечитайте диски перед применением, иначе ваши правки перезапишут чужие.',
  presenceOpenBy: 'открыто у {users}',
  editorReloadTimeout: 'Свежей инвентаризации пока нет — устройство занято или офлайн. Показано последнее доступное.',
  copySerial: 'Скопировать серийный номер',
  copied: 'Скопировано в буфер',
  copyFailed: 'Не удалось скопировать',
  editorDisksRefreshed: 'Диски перечитаны — показано новое состояние.',
  editorPostApplyWaiting: 'Ждём переинвентаризации дисков с новыми значениями…',
  editorPostApplyWaitingHint:
    'Прошивка прошла успешно, список обновится сам, как только устройство перечитает диски. Если был запрошен power-cycle, диски сначала перезагружаются — это может занять до ~2,5 минут. Вручную перезагружать не нужно.',
  editorDevice: 'Устройство',
  editorApplying: 'Прошивка — запись на устройство…',
  editorApplyingHint:
    'Список дисков заблокирован, пока устройство не отчитается. После записи каждый диск передёргивается и возвращается в строй за ~10–15с.',
  editorApplyOk: 'Изменённые диски успешно прошиты',
  editorApplyFailed: 'Часть изменённых дисков не прошилась',
  editorApplyCount: 'Прошито изменённых: {flashed}; без изменений ({unchanged}) не трогали.',
  pcBackInSec: 'передёрнут, вернулся за {sec}с',
  pcNoReturn: 'передёрнут, не вернулся вовремя',
  pcCycleDiskHint: 'Передёрнуть питание этого диска (перезагрузит рабочий диск)',
  pcCycleSelected: 'Передёрнуть выбранные',
  pcCycleSelectedHint: 'Передёрнуть питание выбранных дисков (перезагрузит рабочие диски)',
  pcSlotUnknown: 'Слот неизвестен — обновите агент, чтобы передёрнуть этот диск',
  pcConfirmTitle: 'Передёрнуть питание дисков?',
  pcConfirmWarning: 'Это перезагрузит {count} рабочих диск(ов). Они уйдут в офлайн на ~10–15с во время перезапуска.',
  pcConfirmSlots: 'Диски',
  pcConfirmOk: 'Передёрнуть',
  pcCycleRequested: 'Передёргиваю {serials} — ожидаю возврата дисков…',
  pcCycleTimeout: 'Диски не переинвентаризировались вовремя — возможно, они ещё возвращаются. Обновите, чтобы проверить.',
  pcWaiting: 'Передёргиваю {serials} — ожидаю возврата дисков…',
  pcWaitingHint:
    'Диски перезагружаются, и агент переинвентаризирует их после возврата (~10–15с каждый). Этот вид автоматически переключится на свежую сессию.',
  pcFailed: 'передёргивание не удалось',
  pcSkipped: 'передёргивание пропущено',
  downloadGroupBackup: 'Скачать бэкап группы (.hwrsv)',
  devicePresence: 'Присутствие',
  deviceApprove: 'Одобрить',
  deviceDeny: 'Отклонить',
  deviceDenyConfirm: 'Отклонить эту заявку на подключение?',
  deviceOnline: 'на связи',
  deviceOffline: 'не на связи',
  deviceVersion: 'Версия',
  devicePendingCount: '{n} ожидают одобрения',
  deviceUpdateBtn: 'Обновить',
  deviceUpdateDialogTitle: 'Обновление {label}',
  deviceUpdateCurrentVersion: 'Текущая версия',
  deviceUpdateSelectVersion: 'Целевая версия',
  deviceUpdateLatestTag: 'последняя',
  deviceUpdateMandatoryTag: 'обязательное',
  deviceUpdateWhatsNew: 'Что нового',
  deviceUpdateNoNotes: 'Для этой версии нет описания изменений.',
  deviceUpdateNoReleases: 'Пока нет опубликованных релизов.',
  deviceUpdatePending:
    'Автоматическая доставка на устройство пока не реализована — здесь только предпросмотр того, что будет отправлено.',
  deviceUpdateNowBtn: 'Обновить сейчас',
  deviceUpdateRequested: 'Обновление запрошено — устройство применит его на ближайшем heartbeat.',
  deviceUpdateNowHint:
    'На ближайшем heartbeat устройство скачает последний опубликованный релиз, проверит подпись и применит его (с авто-откатом при сбое).',
  deviceUpdateSuperadminOnly: 'Запустить доставку обновления на устройство может только суперадмин.',
  deviceLastError: 'Последняя ошибка',
  deviceErrorDialogTitle: 'Ошибка на {label}',
  deviceErrorAt: 'Время возникновения',
  deviceRecentLog: 'Недавний лог',
  deviceShowLog: 'Показать лог',
  deviceVerboseEnable: 'Подробный лог',
  deviceVerboseFor: 'Подробный на {n} мин',
  deviceVerboseActive: 'Подробный: осталось {n} мин',
  deviceVerboseDisable: 'Стоп',
  deviceVerboseHint: 'Включить подробное логирование на устройстве на ограниченное время.',
  deviceLogDialogTitle: 'Лог — {label}',
  deviceNoLogs: 'Пока нет записей',
  deviceLogLoadMore: 'Загрузить ещё',
  deviceLogAllLoaded: 'всё загружено',
  deviceOwnerUnassigned: 'Не назначен',
  deviceOwnerChange: 'Изменить',
  deviceOwnerAssign: 'Назначить владельцев',
  deviceOwnerDialogTitle: 'Владельцы {label}',
  deviceOwnerSave: 'Сохранить',
  deviceOwnerNone: 'Нет владельцев',
  dialogClose: 'Закрыть',
  auditTitle: 'Логи',
  auditAction: 'Действие',
  auditUserDevice: 'Пользователь / Устройство',
  auditIp: 'IP',
  auditDetails: 'Подробности',
  auditTime: 'Время',
  auditExportCsv: 'Экспорт CSV',
  recoveryBannerTitle: 'Мало резервных кодов',
  recoveryBannerBody: 'Осталось всего {remaining} резервных TOTP-кодов — перегенерируйте до того, как закончатся.',
  recoveryBannerCta: 'В Аккаунт',
  sessionDelete: 'Удалить',
  sessionDeleteConfirm: 'Удалить эту сессию навсегда?',
  previewBtn: 'Предпросмотр',
  previewFailed: 'Ошибка предпросмотра',
  previewTitle: 'Предпросмотр патча (изменения не применяются)',
  previewBytesChanged: 'изменённых байт',
  previewRegion: 'регион',
  previewFirstOffset: 'смещение',
  quotaBalance: 'Квота',
  quotaExhaustedTitle: 'Квота исчерпана',
  quotaShortTitle: 'Не хватает квоты на все выбранные диски',
  quotaFlashCount: 'Будет прошито дисков: {n} · доступно квоты: {available}',
  quotaShortHint:
    'Каждый прошитый диск тратит 1 единицу квоты. Снимите галочки, чтобы их было не больше {available}, или попросите админа выделить ещё.',
  quotaExhaustedHint: 'Квота на прошивку закончилась — попросите админа выделить ещё. Apply заблокирован.',
  quotaLowTitle: 'Мало квоты: осталось {n}',
  quotaLowHint: 'Попросите админа выделить ещё, пока не закончилась.',
  quotaTotal: 'Всего',
  quotaUsed: 'Использовано',
  quotaRemaining: 'Остаток',
  quotaExpires: 'Истекает',
  quotaNote: 'Заметка',
  quotaRevocationsTitle: 'Отозвано',
  quotaRevokedAmount: 'Кол-во',
  quotaRevokedBy: 'Кем',
  quotaGranted: 'Выдана',
  quotaGrantedBy: 'Кем выдана',
  quotaGrantBtn: 'Выдать квоту',
  quotaGrantTitle: 'Выдать новую квоту',
  quotaCount: 'Юнитов',
  quotaExpiry: 'Через сколько дней истечёт (пусто = бессрочно)',
  quotaSubmit: 'Выдать',
  quotaRevokeBtn: 'Отозвать квоту',
  quotaRevokeTitle: 'Отозвать квоту',
  quotaRevokeSubmit: 'Отозвать',
  quotaRevokeAvailable: 'Доступно к отзыву: {available}',
  // Навигация (моб.)
  navMenu: 'Меню',
  navOpenMenu: 'Открыть меню навигации',
  // Панель
  navDashboard: 'Панель',
  dashKpiFlashesToday: 'Прошивок сегодня',
  dashKpiFlashes7d: 'Прошивок (7д)',
  dashKpiFlashes30d: 'Прошивок (30д)',
  dashKpiFlashesTotal: 'Прошивок всего',
  dashKpiFlashesUnique: 'Уникальных дисков',
  dashKpiQuotaGranted: 'Выдано квоты',
  dashKpiQuotaConsumed: 'Использовано квоты',
  dashKpiQuotaAvailable: 'Доступно квоты',
  dashKpiActiveDevices: 'Активных устройств',
  dashKpiDevicesOnline: 'Устройств онлайн / всего',
  dashKpiActiveClients: 'Активных клиентов',
  dashChartTitle: 'Прошивки по дням',
  dashChartEmpty: 'Нет прошивок за период',
  dashChartFlashesOn: 'Прошивок: {count} — {date}',
  dashRange7: '7д',
  dashRange30: '30д',
  dashRange90: '90д',
  dashTopClients: 'Топ клиентов по прошивкам',
  dashColClient: 'Клиент',
  dashTopDevices: 'Топ девайсов по прошивкам',
  dashColDevice: 'Девайс',
  dashDeviceDeleted: 'удалён',
  dashColFlashes: 'Прошивки',
  dashColUniqueDisks: 'Уник. дисков',
  dashColAvailable: 'Доступно',
  dashColUsed: 'Использовано',
  dashTotal: 'Итого',
  dashRefresh: 'Обновить',
  // Дашборд — хранилище и диски
  dashStorageTitle: 'Хранилище и диски',
  dashDisksByType: 'Прошитые диски по типам',
  dashDiskNvme: 'NVMe',
  dashDiskSas25: 'SAS 2.5"',
  dashDiskSas35: 'SAS 3.5"',
  dashDiskUnknown: 'Неизвестно',
  dashDisksTotal: 'Всего',
  dashBackupsCount: 'Бэкапы',
  dashBackupsSize: 'Объём бэкапов',
  dashDbSize: 'Размер БД',
  dashServerFree: 'Свободно на сервере',
  dashServerUsedOf: 'занято {used} из {total}',
  // История квоты
  quotaHistoryTitle: 'История квоты',
  quotaHistoryEmpty: 'История квоты пуста',
  quotaLedgerDate: 'Дата',
  quotaLedgerType: 'Тип',
  quotaLedgerDelta: 'Изменение',
  quotaLedgerBalance: 'Баланс после',
  quotaLedgerReason: 'Причина',
  quotaLedgerDisk: 'Диск / устройство',
  quotaTypeTopup: 'пополнение',
  quotaTypeSpend: 'списание',
  quotaTypeAdjust: 'корректировка',
  quotaTypeExpire: 'истечение',
  // Изменение пользователя
  userEdit: 'Изменить',
  userEditTitle: 'Изменить пользователя',
  userEditPasswordHint: 'Оставьте пустым, чтобы не менять пароль',
  userEditOk: 'Пользователь {email} обновлён',
  userSave: 'Сохранить',
  confirmApplyTitle: 'Подтверждение изменений',
  confirmApplyIntro: 'Эти изменения будут отправлены на устройство и записаны на диски. За каждый прошитый диск списывается одна единица квоты.',
  confirmApplyEmpty: 'Изменений нет.',
  confirmApplyPowerCycle: 'Паверцикл дисков после прошивки',
  confirmApplyPowerCycleHint:
    'В конце передёргивает питание каждого диска, чтобы контроллер перечитал Reserve Area и подхватил новую идентичность. Без этого контроллер будет показывать старый Level/BOM, пока диск не переустановят.',
  confirmApplyTypeYes: 'Введите YES для подтверждения прошивки дисков: {n}',
  confirmApplyButton: 'Подтвердить и применить',
  confirmApplyCancel: 'Отмена',
  changeDisk: 'Диск',
  changeField: 'Поле',
  changeOld: 'Текущее',
  changeNew: 'Новое',
  navAccount: 'Аккаунт',
  accountTitle: 'Ваш аккаунт',
  totpSection: 'Двухфакторная аутентификация',
  totpEnabledNote: '2FA ВКЛЮЧЕНА. При каждом входе запрашивается 6-значный код.',
  totpDisabledNote: '2FA ВЫКЛЮЧЕНА. Включите её, чтобы защитить аккаунт от атак на пароль.',
  totpBeginBtn: 'Включить 2FA',
  totpDisableBtn: 'Отключить 2FA',
  totpSecretLabel: 'Отсканируйте QR или введите секрет в приложение-аутентификатор (Google Authenticator, Authy, 1Password …):',
  totpSecretCopy: 'Скопировать секрет',
  totpCodeInput: 'Введите 6-значный код из приложения',
  totpVerifyBtn: 'Проверить и включить',
  totpFailed: 'Неверный код. Попробуйте ещё раз — код меняется каждые 30 секунд.',
  totpEnabledOk: '2FA включена. Выйдите и войдите снова, чтобы проверить.',
  deviceRevokeBtn: 'Отозвать',
  deviceReactivateBtn: 'Восстановить',
  deviceRevokeConfirm: 'Отозвать устройство? Все последующие пакеты от него будут отвергаться с 403. Аудит и бэкапы дисков сохранятся.',
  deviceReactivateConfirm: 'Восстановить устройство?',
  deviceDeleteBtn: 'Удалить',
  deviceMigrateIdBtn: 'Мигрировать ID',
  deviceMigrateIdHint:
    'Перенести контроллер на новый device id (его стабильный аппаратный id) вместе со всей историей.',
  deviceMigrateIdTitle: 'Миграция device ID',
  deviceMigrateIdBody:
    'Укажите целевой device id — аппаратный id контроллера (показан как Device ID на его локальной странице или в конфиге hw_fingerprint). Вся история (сессии, бэкапы, реестр прошивок, логи) будет перепривязана. Если id свободен — строка переименовывается (ключи сохраняются, переодобрение не нужно); если id уже существует — история сливается в него.',
  deviceMigrateIdCurrent: 'Текущий id:',
  deviceMigrateIdOk: 'Мигрировать',
  deviceMigrateDone: 'Миграция ({mode}) — перенесено строк истории: {n}.',
  deviceDeleteConfirm:
    'Удалить это отозванное устройство навсегда? Его сессии и операционные данные будут удалены; бэкапы дисков и аудит сохранятся.',
  authSessionsTitle: 'Активные сессии входа',
  authSessionId: 'Сессия',
  authSessionCreated: 'Создана',
  authSessionExpires: 'Истекает',
  authSessionCurrent: 'Текущая',
  authSessionRevoke: 'Отозвать',
  authSessionRevokeConfirm: 'Отозвать эту сессию?',
  authSessionDevice: 'Устройство',
  authSessionIp: 'IP-адрес',
  authSessionCount: 'Сессий',
  authSessionLastActive: 'Последний вход',
  authSessionUnknownDevice: 'Неизвестное устройство',
  authSessionRevokeGroup: 'Отозвать',
  authSessionRevokeGroupConfirm: 'Отозвать все сессии с этого устройства?',
  authSessionRevokeOthers: 'Отозвать все остальные',
  authSessionRevokeOthersConfirm: 'Выйти на всех устройствах, кроме этого браузера?',
  recoveryCodesTitle: 'Резервные коды',
  recoveryCodesIntro:
    'Одноразовые коды для входа, если потеряете доступ к TOTP-приложению. Напечатайте или сохраните в безопасном месте — больше показаны не будут.',
  recoveryCodesRemaining: 'Осталось кодов',
  recoveryCodesIssuedNote: 'Коды показаны один раз и больше не отобразятся. Сохраните их.',
  recoveryCodesCopy: 'Копировать в буфер',
  recoveryCodesDownload: 'Скачать .txt',
  recoveryCodesHide: 'Сохранил',
  recoveryCodesRegenBtn: 'Перегенерировать',
  recoveryCodesRegenConfirm: 'Это отменит все существующие коды.',
  errorBoundaryTitle: 'Что-то пошло не так',
  errorBoundaryIntro:
    'Дашборд столкнулся с непредвиденной ошибкой. Мы отправили обезличенный отчёт команде администраторов.',
  errorBoundaryReload: 'Перезагрузить страницу',
  errorBoundaryDetails: 'Технические детали',
  loginLocked: 'Блокировка',
  loginLockedExplain:
    'Слишком много неудачных попыток. Попробуйте через {time}, или обратитесь к админу, если забыли пароль.',
  // Управление пользователями
  usersTitle: 'Пользователи',
  userEmail: 'Email',
  userRole: 'Роль',
  userCreated: 'Создан',
  userActions: 'Действия',
  userCreateTitle: 'Новый пользователь',
  userCreateBtn: 'Создать',
  userNewBtn: 'Новый пользователь',
  userResetPw: 'Сбросить пароль',
  userResetPwPrompt: 'Новый пароль для {email}:',
  userResetPwOk: 'Пароль сброшен для {email}',
  userDelete: 'Удалить',
  userDeleteConfirm: 'Удалить пользователя {email}? Действие необратимо.',
  userRoleAdmin: 'админ',
  userRoleClient: 'клиент',
  userRoleSuperadmin: 'суперадмин',
  userNote: 'Примечание',
  userNotePlaceholder: 'Примечание (организация, контакт, назначение)…',
  auditClearBtn: 'Очистить журнал',
  auditClearConfirm: 'Безвозвратно удалить ВСЕ записи журнала аудита? Отменить нельзя.',
  auditClearedOk: 'Журнал аудита очищен ({removed} записей)',
  backupDelete: 'Удалить',
  backupDeleteConfirm: 'Безвозвратно удалить этот бэкап?',
  backupDeletedOk: 'Бэкап удалён',
  userPasswordPlaceholder: 'пароль',
  userPasswordMinHint: 'Не менее 8 символов.',
  userYouTag: '(вы)',
  userActionFailed: 'Не удалось выполнить действие',
  // Групповое изменение дисков
  batchTitle: 'Групповое изменение',
  batchHint: 'Заполните нужные поля и примените их сразу ко всем выбранным дискам.',
  batchApply: 'Применить к выбранным ({n})',
  batchSelectAll: 'Выбрать все',
  batchClear: 'Снять выбор',
  batchSelected: 'выбрано: {n}',
  batchNothingSelected: 'Сначала выберите хотя бы один диск.',
  diskModified: 'изменён',
  clientEmptyHint:
    'Пусто? Диски становятся видны после того, как администратор привяжет устройство к вашему аккаунту. Обратитесь к администратору.',
  // Табличный редактор дисков
  bulkFillTitle: 'Групповое заполнение',
  bulkFillValue: 'значение',
  bulkFillApply: 'Заполнить выбранные ({n})',
  barcodeCounterApply: 'Применить счётчик ({n})',
  colVisibility: 'Столбцы',
  colType: 'Тип',
  colOrder: 'Порядок',
  colFrame: 'Фрейм',
  colSlot: 'Слот',
  buttonHex: 'Hex',
  hexDrawerTitle: 'Hex-просмотр — {sn}',
  moveUp: 'Вверх',
  moveDown: 'Вниз',
  barcodeManual: 'вручную',
  barcodeSyncDate: 'синхр. дату',
  barcodeSyncDateHint:
    'Включено по умолчанию: дата в BarCode пересчитывается из поля «Дата производства». Снимите галочку, чтобы сохранённую дату в BarCode диска не трогать.',
  barcodeSyncBom: 'синхр. BOM',
  barcodeSyncBomHint:
    'Включено по умолчанию: часть BarCode (8 симв.) перезаписывается из текущего поля BOM. Снимите галочку, чтобы эти данные в BarCode не трогать.',
  reorderManualHint: 'перетащите за ручку или используйте стрелки для задания порядка итерации',
  dragHandle: 'Перетащите для сортировки',
  undoTooltip: 'Отменить последнее изменение',
  undoButton: 'Отменить',
  // Экспорт / импорт .hwrsv (админ)
  hwrsvExport: 'Экспорт',
  hwrsvImport: 'Импорт',
  hwrsvExportHint: 'Скачать снимок этого диска в виде архива .hwrsv.',
  hwrsvImportHint: 'Загрузить .hwrsv для этого диска — файл загружается как ожидающие изменения (проверьте, затем «Применить»). Автопрошивки нет.',
  hwrsvExportSelected: 'Экспорт выбранных (.hwrsv)',
  hwrsvImportCombined: 'Импорт (.hwrsv)',
  hwrsvImportCombinedHint: 'Загрузить .hwrsv; записи сопоставляются с дисками по серийнику и загружаются как ожидающие изменения (проверьте, затем «Применить»). Автоматически ничего не прошивается.',
  hwrsvExportOk: 'Бэкап скачан.',
  hwrsvImportOk: 'Импортировано и зарегистрировано как бэкап — примените из Истории.',
  hwrsvImportedPending: 'Импортировано дисков как ожидающие изменения: {n} — проверьте и примените.',
  hwrsvExportFailed: 'Ошибка экспорта',
  hwrsvImportFailed: 'Ошибка импорта',
  hwrsvImportedFields: 'Импортировано полей как ожидающие изменения: {n} — проверьте и примените.',
  hwrsvImportedNone: 'Импортировать нечего — файл совпадает с текущими значениями диска.',
  // Имя пользователя / идентичность + прочие метки админа
  userUsername: 'Имя пользователя',
  userEmailOptional: 'Email (необязательно)',
  deviceOwner: 'Владельцы',
  historyDiskSn: 'SN диска',
  historyDiskType: 'Тип',
  historyWhen: 'Дата/Время',
  historyResult: 'Результат',
  flashHistoryTitle: 'История прошивок',
  flashHistoryLastFlash: 'Последняя прошивка',
  flashChanges: 'Изменения',
  flashChangesCount: 'Дисков',
  flashGroupByDevice: 'Группировать по устройству',
  flashPending: 'ожидает',
  flashOkAll: 'OK ({n})',
  flashPartial: '{ok}/{total} OK',
  flashUnknownDevice: 'Неизвестное устройство',
  navFlashHistory: 'История прошивок',
  // Экспорт в XLSX (редактор дисков + история прошивок)
  exportXls: 'Экспорт XLSX',
  exportXlsTip: 'Скачать список дисков в виде таблицы — по две строки на диск: было / стало',
  xlsSheetChanges: 'Изменения',
  xlsSheetHistory: 'История прошивок',
  xlsColSlot: 'Слот',
  xlsColFrame: 'Полка',
  xlsColChanged: 'Изменён',
  xlsWasRow: 'было',
  xlsNowRow: 'стало',
  xlsYes: 'да',
  xlsNo: 'нет',
  logsAllActors: '(все субъекты)',
  logsActionPlaceholder: 'действие…',
  // Hex-просмотр
  hexRegionLabel: 'Регион',
  hexLoading: 'Загрузка…',
  hexParsedTitle: 'Разбор (только чтение)',
  hexParsedRegion: 'Регион',
  hexParsedOffset: 'Смещение',
  hexParsedLength: 'Длина',
  hexBytesUnit: '{n} байт',
  hexLegendBlock: 'граница блока ({n} Б)',
  hexLegendPi: 'PI-метаданные (последние 8 Б)',
  // Брендовый футер
  footerProject: 'Проект',
  footerDocs: 'Документация',
};

const DICTS: Record<Locale, Dict> = { en, ru };

let currentLocale: Locale =
  (localStorage.getItem('locale') as Locale | null) === 'ru' ? 'ru' : 'en';

export function setLocale(loc: Locale): void {
  currentLocale = loc;
  localStorage.setItem('locale', loc);
}

export function getLocale(): Locale {
  return currentLocale;
}

export function t(key: keyof typeof en, vars?: Record<string, string | number>): string {
  const raw = DICTS[currentLocale][key] ?? DICTS.en[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : `{${name}}`,
  );
}
